import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Button from '~/components/Button'
import CheckboxInput from '~/components/CheckboxInput'

export const TermsStep = ({
  onNextStep,
  onPreviousStep,
  termsAcceptedPreviously = false
}: {
  onNextStep: () => void
  onPreviousStep: () => void
  termsAcceptedPreviously?: boolean
}) => {
  const terms = useAppSelector(state => state.terms.terms?.content)
  const { control, watch } = useFormContext()
  const agreementRef = useRef<HTMLDivElement>(null)
  const [buttonTranslateY, setButtonTranslateY] = useState(0)

  const updateScrollButtonPosition = useCallback(() => {
    const agreementElement = agreementRef.current

    if (!agreementElement) return

    const agreementTop = agreementElement.getBoundingClientRect().top + window.scrollY
    const scrollableDistance = document.documentElement.scrollHeight - window.innerHeight
    const scrollDistanceToAgreement = Math.max(agreementTop - window.innerHeight * 0.8, 1)

    if (scrollableDistance <= 0) {
      setButtonTranslateY(100)

      return
    }

    const hideStart = scrollDistanceToAgreement * 0.8
    const hideDistance = Math.max(scrollDistanceToAgreement - hideStart, 1)
    const translateY = Math.min(Math.max(((window.scrollY - hideStart) / hideDistance) * 100, 0), 100)

    setButtonTranslateY(translateY)
  }, [])

  const scrollToAgreement = useCallback(() => {
    agreementRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }, [])

  useEffect(() => {
    updateScrollButtonPosition()

    window.addEventListener('scroll', updateScrollButtonPosition, { passive: true })
    window.addEventListener('resize', updateScrollButtonPosition)

    return () => {
      window.removeEventListener('scroll', updateScrollButtonPosition)
      window.removeEventListener('resize', updateScrollButtonPosition)
    }
  }, [terms, updateScrollButtonPosition])

  if (!terms) return null

  return (
    <div className='md:w-2/3 mx-auto w-full p-4 pb-28'>
      <h1 className='mb-4 text-2xl font-medium text-text-primary'>
        Gebruikersvoorwaarden
      </h1>

      <div
        className='html-content my-4 mx-auto'
        dangerouslySetInnerHTML={{ __html: terms }}
      />

      <div
        ref={agreementRef}
        className='my-8 border-b border-border-default'
      />

      <Controller
        name='termsRead'
        control={control}
        rules={{
          required: true
        }}
        render={({ field }) => (
          <CheckboxInput
            {...field}
            label='Ik ga akkoord met de gebruiksvoorwaarden'
            checked={!!field.value}
            className='mb-8'
            value={field.name}
          />
        )}
      />

      <div className='flex justify-between'>
        {!termsAcceptedPreviously
          ? (
            <Button
              onClick={onPreviousStep}
              variant='outline'
              replaceChildrenWhileLoading={true}
            >
              <FontAwesomeIcon
                icon={['fal', 'chevron-left']}
                className='mr-2'
              />
            Vorige
            </Button>
          )
          : (
            <div className='w-[40px]' /> // Placeholder for consistent layout
          )}

        <Button
          onClick={onNextStep}
          type='button'
          disabled={!watch('termsRead')}
        >
          {!termsAcceptedPreviously ? 'Volgende' : 'Accepteer de voorwaarden'}

          <FontAwesomeIcon
            icon={['fal', 'chevron-right']}
            className='ml-2'
          />
        </Button>
      </div>

      <div
        className={`fixed right-0 bottom-0 left-0 z-40 flex justify-center bg-surface-page p-4 ${buttonTranslateY >= 100 ? 'pointer-events-none' : ''}`}
        style={{ transform: `translateY(${buttonTranslateY}px)` }}
        aria-hidden={buttonTranslateY >= 100}
      >
        <button
          type='button'
          onClick={scrollToAgreement}
          className='rounded-md px-4 py-2 text-center text-action-outline-foreground transition-opacity hover:bg-opacity-90 active:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-focus focus-visible:outline-offset-2'
          aria-label='Direct naar akkoord'
          tabIndex={buttonTranslateY >= 100 ? -1 : 0}
        >
          <span className='block font-medium'>
            Direct naar akkoord
          </span>

          <FontAwesomeIcon
            icon={['fal', 'chevron-down']}
            className='mt-1 text-icon-brand'
          />
        </button>
      </div>
    </div>
  )
}
